import React, { useState } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Select, 
  Space, 
  Typography,
  Table,
  Statistic,
  Progress,
  Tag,
  DatePicker,
  Button,
  Tooltip,
  Empty
} from 'antd';
import { 
  DollarOutlined, 
  RiseOutlined,
  FallOutlined,
  BarChartOutlined,
  ProjectOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { useQuery } from 'react-query';
import axios from 'axios';
import dayjs from 'dayjs';
import { useAuthStore } from '../../stores/authStore';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

const { Option } = Select;
const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

/**
 * 盈亏统计分析页面组件
 * 显示项目预算、实际成本、盈亏情况等统计信息
 */
const ProfitLossAnalysis = () => {
  const { isAuthenticated, user } = useAuthStore();
  const [typeFilter, setTypeFilter] = useState('');
  const [designLeaderFilter, setDesignLeaderFilter] = useState(''); // 设计负责人筛选
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(3, 'month'),
    dayjs()
  ]);

  // 获取用户列表（用于设计负责人选择）
  const { data: usersData } = useQuery(
    'users',
    () => axios.get('/users').then(res => res.data.data),
    { enabled: isAuthenticated }
  );

  // 获取项目盈亏数据
  const { data: profitLossData, isLoading } = useQuery(
    ['profitLossData', typeFilter, designLeaderFilter, dateRange],
    () => axios.get('/projects/profit-loss', {
      params: {
        type: typeFilter || undefined,
        designLeaderId: designLeaderFilter || undefined,
        startDate: dateRange[0]?.format('YYYY-MM-DD'),
        endDate: dateRange[1]?.format('YYYY-MM-DD'),
        limit: 100
      }
    }).then(res => res.data.data),
    { 
      refetchInterval: 30000,
      enabled: isAuthenticated && !!dateRange[0] && !!dateRange[1]
    }
  );

  /**
   * 获取项目类型中文名称
   */
  const getTypeText = (type) => {
    const texts = {
      bidding: '投标',
      application: '申报', 
      deepening: '深化',
      consultation: '咨询',
      other: '其他'
    };
    return texts[type] || type;
  };



  /**
   * 获取盈亏状态颜色
   */
  const getProfitColor = (profit) => {
    if (profit > 0) return '#52c41a'; // 绿色 - 盈利
    if (profit < 0) return '#ff4d4f'; // 红色 - 亏损
    return '#faad14'; // 黄色 - 持平
  };

  /**
   * 获取盈亏状态文本
   */
  const getProfitStatus = (profit) => {
    if (profit > 0) return '盈利';
    if (profit < 0) return '亏损';
    return '持平';
  };

  // 计算汇总统计数据
  const summaryStats = {
    totalBudget: profitLossData?.projects?.reduce((sum, p) => sum + (p.budget || 0), 0) || 0,
    totalActualCost: profitLossData?.projects?.reduce((sum, p) => sum + (p.actualCost || 0), 0) || 0,
    totalProfit: 0,
    profitMargin: 0,
    profitableProjects: 0,
    lossProjects: 0
  };

  summaryStats.totalProfit = summaryStats.totalBudget - summaryStats.totalActualCost;
  summaryStats.profitMargin = summaryStats.totalBudget > 0 ? 
    (summaryStats.totalProfit / summaryStats.totalBudget * 100) : 0;
  
  if (profitLossData?.projects) {
    summaryStats.profitableProjects = profitLossData.projects.filter(p => 
      (p.budget || 0) - (p.actualCost || 0) > 0
    ).length;
    summaryStats.lossProjects = profitLossData.projects.filter(p => 
      (p.budget || 0) - (p.actualCost || 0) < 0
    ).length;
  }

  // 表格列定义
  const columns = [
    {
      title: '项目名称',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <Text strong>{text}</Text>
        </Tooltip>
      )
    },
    {
      title: '项目类型',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type) => (
        <Tag color="blue">{getTypeText(type)}</Tag>
      )
    },
    {
      title: '设计负责人',
      dataIndex: 'designLeader',
      key: 'designLeader',
      width: 100,
      render: (leader, record) => {
        // 显示设计负责人姓名
        return leader?.realName || record.designLeaderName || '未指定';
      }
    },
    {
      title: '项目预算',
      dataIndex: 'budget',
      key: 'budget',
      width: 120,
      align: 'right',
      render: (budget) => (
        <Text>¥{(budget || 0).toLocaleString()}</Text>
      )
    },
    {
      title: '实际成本',
      dataIndex: 'actualCost',
      key: 'actualCost',
      width: 120,
      align: 'right',
      render: (cost) => (
        <Text>¥{(cost || 0).toLocaleString()}</Text>
      )
    },
    {
      title: '盈亏金额',
      key: 'profit',
      width: 120,
      align: 'right',
      render: (_, record) => {
        const profit = (record.budget || 0) - (record.actualCost || 0);
        return (
          <Text style={{ color: getProfitColor(profit) }}>
            {profit >= 0 ? '+' : ''}¥{profit.toLocaleString()}
          </Text>
        );
      }
    },
    {
      title: '利润率',
      key: 'profitMargin',
      width: 100,
      align: 'right',
      render: (_, record) => {
        const profit = (record.budget || 0) - (record.actualCost || 0);
        const margin = record.budget > 0 ? (profit / record.budget * 100) : 0;
        return (
          <Text style={{ color: getProfitColor(profit) }}>
            {margin.toFixed(1)}%
          </Text>
        );
      }
    },
    {
      title: '盈亏状态',
      key: 'profitStatus',
      width: 100,
      align: 'center',
      render: (_, record) => {
        const profit = (record.budget || 0) - (record.actualCost || 0);
        const status = getProfitStatus(profit);
        const color = getProfitColor(profit);
        return (
          <Tag color={color === '#52c41a' ? 'success' : color === '#ff4d4f' ? 'error' : 'warning'}>
            {status}
          </Tag>
        );
      }
    },
    {
      title: '进度',
      dataIndex: 'progress',
      key: 'progress',
      width: 120,
      render: (progress) => (
        <Progress 
          percent={progress || 0} 
          size="small"
          status={progress === 100 ? 'success' : 'active'}
        />
      )
    }
  ];

  // 准备图表数据
  const chartData = profitLossData?.projects?.map(project => ({
    name: project.name.length > 8 ? project.name.substring(0, 8) + '...' : project.name,
    budget: project.budget || 0,
    actualCost: project.actualCost || 0,
    profit: (project.budget || 0) - (project.actualCost || 0)
  })) || [];

  return (
    <div style={{ padding: '24px' }}>
      <Card 
        title={
          <Space>
            <BarChartOutlined />
            项目盈亏统计分析
          </Space>
        }
        className="status-card"
      >
        {/* 筛选条件 */}
        <div style={{ marginBottom: '24px' }}>
          <Space wrap>
            <Select
              placeholder="项目类型"
              allowClear
              style={{ width: 120 }}
              value={typeFilter}
              onChange={setTypeFilter}
            >
              <Option value="bidding">投标</Option>
              <Option value="application">申报</Option>
              <Option value="deepening">深化</Option>
              <Option value="consultation">咨询</Option>
              <Option value="other">其他</Option>
            </Select>
            
            <Select
              placeholder="设计负责人"
              allowClear
              showSearch
              style={{ width: 150 }}
              value={designLeaderFilter}
              onChange={setDesignLeaderFilter}
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              onClear={() => setDesignLeaderFilter('')}
            >
              {usersData?.users?.filter(user => 
                ['designer', 'manager'].includes(user.role)
              ).map(user => (
                <Option key={user.id} value={user.id}>
                  {user.realName}
                </Option>
              ))}
            </Select>
            
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              format="YYYY-MM-DD"
              placeholder={['开始日期', '结束日期']}
            />
            
            <Button 
              type="primary" 
              onClick={() => {
                setTypeFilter('');
                setDesignLeaderFilter('');
                setDateRange([dayjs().subtract(3, 'month'), dayjs()]);
              }}
            >
              重置筛选
            </Button>
          </Space>
        </div>

        {/* 汇总统计卡片 */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} lg={6}>
            <Card className="status-card">
              <Statistic
                title="总预算"
                value={summaryStats.totalBudget}
                precision={0}
                prefix={<DollarOutlined style={{ color: '#1890ff' }} />}
                suffix="元"
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          
          <Col xs={24} sm={12} lg={6}>
            <Card className="status-card">
              <Statistic
                title="总成本"
                value={summaryStats.totalActualCost}
                precision={0}
                prefix={<DollarOutlined style={{ color: '#faad14' }} />}
                suffix="元"
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          
          <Col xs={24} sm={12} lg={6}>
            <Card className="status-card">
              <Statistic
                title="总盈亏"
                value={summaryStats.totalProfit}
                precision={0}
                prefix={
                  summaryStats.totalProfit >= 0 ? 
                    <RiseOutlined style={{ color: '#52c41a' }} /> : 
                    <FallOutlined style={{ color: '#ff4d4f' }} />
                }
                suffix="元"
                valueStyle={{ color: getProfitColor(summaryStats.totalProfit) }}
              />
            </Card>
          </Col>
          
          <Col xs={24} sm={12} lg={6}>
            <Card className="status-card">
              <Statistic
                title="利润率"
                value={summaryStats.profitMargin}
                precision={1}
                suffix="%"
                valueStyle={{ color: getProfitColor(summaryStats.totalProfit) }}
              />
            </Card>
          </Col>
        </Row>

        {/* 盈亏项目统计 */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={8}>
            <Card className="status-card">
              <Statistic
                title="盈利项目"
                value={summaryStats.profitableProjects}
                suffix="个"
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          
          <Col xs={24} sm={8}>
            <Card className="status-card">
              <Statistic
                title="亏损项目"
                value={summaryStats.lossProjects}
                suffix="个"
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Card>
          </Col>
          
          <Col xs={24} sm={8}>
            <Card className="status-card">
              <Statistic
                title="项目总数"
                value={profitLossData?.projects?.length || 0}
                suffix="个"
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
        </Row>

        {/* 图表区域 */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} lg={12}>
            <Card title="项目预算与成本对比" size="small">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="name" 
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    label={{ value: '金额(元)', angle: -90, position: 'insideLeft' }}
                  />
                  <RechartsTooltip 
                    formatter={(value, name) => [
                      `¥${value.toLocaleString()}`, 
                      name === 'budget' ? '预算' : name === 'actualCost' ? '成本' : '盈亏'
                    ]}
                  />
                  <Bar dataKey="budget" fill="#1890ff" name="预算" />
                  <Bar dataKey="actualCost" fill="#faad14" name="成本" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          
          <Col xs={24} lg={12}>
            <Card title="项目盈亏趋势" size="small">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="name" 
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    label={{ value: '盈亏(元)', angle: -90, position: 'insideLeft' }}
                  />
                  <RechartsTooltip 
                    formatter={(value) => [`¥${value.toLocaleString()}`, '盈亏金额']}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="profit" 
                    stroke="#52c41a" 
                    strokeWidth={2}
                    dot={{ fill: '#52c41a', strokeWidth: 2, r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

        {/* 项目详细列表 */}
        <Card title="项目盈亏详细列表" size="small">
          <Table
            columns={columns}
            dataSource={profitLossData?.projects || []}
            rowKey="id"
            loading={isLoading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 个项目`
            }}
            scroll={{ x: 1200 }}
            locale={{
              emptyText: (
                <Empty 
                  description="暂无项目数据" 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )
            }}
          />
        </Card>
      </Card>
    </div>
  );
};

export default ProfitLossAnalysis;